import { useQueryClient } from "@tanstack/react-query";

import type { ActionItem } from "@elation/api-client/src/stitch-web";
import {
  getListActionItemsForEncounterQueryKey,
  useUpdateActionItemStatus,
} from "@elation/api-client/src/stitch-web";

import { ActionItemEntity } from "@src/features/action-items/domain";
import { events } from "@src/shared/utils/observability/events";

type CachedActionItemList = {
  response?: unknown;
  data: {
    items: ActionItem[];
  };
};

type ToggleContext = { previous: CachedActionItemList | undefined };

export interface UseActionItemMutationsResult {
  toggleActionItem: (item: ActionItem) => void;
}

export function useActionItemMutations(
  encounterId: number,
): UseActionItemMutationsResult {
  const queryClient = useQueryClient();
  const queryKey = getListActionItemsForEncounterQueryKey(encounterId);

  const { mutate } = useUpdateActionItemStatus<unknown, ToggleContext>({
    mutation: {
      onMutate: async (vars): Promise<ToggleContext> => {
        await queryClient.cancelQueries({ queryKey });
        const previous =
          queryClient.getQueryData<CachedActionItemList>(queryKey);
        queryClient.setQueryData<CachedActionItemList>(queryKey, (old) => ({
          ...(old ?? { data: { items: [] } }),
          data: {
            ...old?.data,
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.id ? { ...i, status: vars.data.status } : i,
            ),
          },
        }));
        return { previous };
      },
      onSuccess: (envelope, vars) => {
        const serverItem = envelope.data;
        queryClient.setQueryData<CachedActionItemList>(queryKey, (old) => ({
          ...(old ?? { data: { items: [] } }),
          data: {
            ...old?.data,
            items: (old?.data?.items ?? []).map((i) =>
              i.id === vars.id ? serverItem : i,
            ),
          },
        }));
        const payload = {
          action_item_id: serverItem.id,
          category: serverItem.category,
        };
        if (serverItem.status === "completed") {
          events.actionItemCompleted.track(payload);
        } else {
          events.actionItemReopened.track(payload);
        }
      },
      onError: (err, vars, context) => {
        console.error("[useActionItemMutations] toggle failed", {
          encounterId,
          itemId: vars.id,
          status: vars.data.status,
          error: err,
        });
        if (!context) return;
        queryClient.setQueryData(queryKey, context.previous);
      },
    },
  });

  const toggleActionItem = (item: ActionItem) => {
    const nextStatus = ActionItemEntity.nextStatus(item);
    if (!nextStatus) return;
    mutate({ id: item.id, data: { status: nextStatus } });
  };

  return { toggleActionItem };
}
